import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

interface DialogResult<TData = unknown> {
  success: boolean;
  data?: TData;
}

export interface DialogProps<T = unknown> {
  onResolve: (result: DialogResult<T>) => void;
  onReject: (result: DialogResult<T>) => void;
}

interface DialogInstance<
  T extends React.ComponentType<DialogProps> = React.ComponentType<DialogProps>
> {
  id: string;
  props: Partial<T>;
  component: T;
  resolve: (result: DialogResult) => void;
}

interface DialogContextType {
  openDialog: <T extends React.ComponentType<DialogProps>>(
    component: T,
    props?: Partial<T> // Optional props to pass
  ) => Promise<DialogResult>;
  closeDialog: (id: string) => void;
}
const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const AsyncDialogContext: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [dialogs, setDialogs] = useState<DialogInstance[]>([]);
  const dialogCounter = useRef(0);

  const openDialog = useCallback(
    <T extends React.ComponentType<DialogProps>>(
      component: T,
      props: Partial<T> = {} // Accept additional props
    ): Promise<DialogResult> => {
      return new Promise<DialogResult>((resolve) => {
        const id = `dialog-${++dialogCounter.current}`;

        const newDialog: DialogInstance = {
          id,
          component,
          props, // Store the props
          resolve,
        };

        setDialogs((prev) => [...prev, newDialog]);
      });
    },
    []
  );

  const closeDialog = useCallback((id: string) => {
    setDialogs((prev) => prev.filter((dialog) => dialog.id !== id));
  }, []);

  const handleResolve = useCallback(
    (id: string, result: DialogResult) => {
      const dialog = dialogs.find((d) => d.id === id);
      if (dialog) {
        dialog.resolve(result);
        closeDialog(id);
      }
    },
    [dialogs, closeDialog]
  );

  const handleReject = useCallback(
    (id: string, result: DialogResult) => {
      const dialog = dialogs.find((d) => d.id === id);
      if (dialog) {
        dialog.resolve(result);
        closeDialog(id);
      }
    },
    [dialogs, closeDialog]
  );

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {dialogs.map(({ id, component: Component, props }) =>
        createPortal(
          <Component
            key={id}
            {...props} // Spread the additional props
            onResolve={(result) => handleResolve(id, result)}
            onReject={(result) => handleReject(id, result)}
          />,
          document.body
        )
      )}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const context = useContext(DialogContext);

  if (!context) {
    throw new Error('useDialog must be used within a AsyncDialogContext');
  }

  return context;
};

/**
 * Our current dialogs have an animation that triggers when the prop `open` goes
 * from false -> true. Unfortunately when they just suddenly appear, that animation
 * doesn't play
 *
 * This is a workaround to ensure that the animations trigger.
 */
export function useOpenDialogAnimation() {
  const [isOpen, setIsOpen] = React.useState(false);

  useEffect(() => {
    setIsOpen(true);
  }, []);

  return isOpen;
}
